/**
 * Classic local math: insert a non-revenue positioning ferry when a scheduled
 * charter's tail is not already at origin. Curfew-aware STD/STA. Grok may see
 * the resulting legs — it must not invent the times.
 */
import { buildRepositionDraft, MIN_TURN_MIN } from "./assignment";
import { curfewClashMessage, ferryTimesLegal, icaoKey, type AirportHours } from "./curfew";
import { formatUtcDateTime } from "./clock";
import { confirmRequestFor, recordSlip, type SlipConfirmRequest } from "./slipJournal";
import type { AircraftSpec, DraftMission, Mission, SceneryAirport, SlipCause } from "./types";

export const FERRY_PAD_MIN = 20;
export const FERRY_LOOKBACK_H = 36;
const STEP_MS = 5 * 60_000;

export type PlanCharterFerryInput = {
  spec: AircraftSpec;
  fromIcao: string | null | undefined;
  toIcao: string;
  charterStd: string | Date;
  scenery: SceneryAirport[];
  now?: Date;
  linkRequestId?: string;
  /** Charter ticket this ferry exists for — trip P&L rolls the cost in. */
  parentMissionId?: string;
  /** When false, do not build a ferry (already at origin). */
  needed?: boolean;
  existing?: ReadonlyArray<Mission>;
  /** Company curfew override. Omitted keeps the built-in quiet-hours table. */
  hoursFor?: (icao: string | null | undefined) => AirportHours;
};

export type PlanCharterFerryResult = {
  needed: boolean;
  ferry: Mission | null;
  reused: boolean;
  shifted: boolean;
  warning?: string;
  fromIcao: string | null;
  toIcao: string;
  cancelMissionIds: string[];
};

function asDate(value: string | Date): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
}

function sameIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  return icaoKey(a) === icaoKey(b) && Boolean(icaoKey(a));
}

function isOpenFerry(m: Pick<Mission, "flightType" | "status" | "isMaintenance">): boolean {
  return m.flightType === "ferry" && !m.isMaintenance && (m.status === "pending" || m.status === "in-progress");
}

/** Inbound positioning (current field → charter origin), not the empty return. */
export function isInboundPositioningFerry(
  m: Pick<Mission, "flightType" | "status" | "isMaintenance" | "originIcao" | "destIcao" | "inboxRequestId">,
  charter: { originIcao: string; destIcao: string; id?: string },
): boolean {
  if (!isOpenFerry(m)) return false;
  if (!sameIcao(m.destIcao, charter.originIcao)) return false;
  if (sameIcao(m.originIcao, charter.destIcao)) return false;
  return true;
}

export function findOpenInboundFerry(
  missions: ReadonlyArray<Mission>,
  requestId: string | undefined,
  fromIcao: string,
  toIcao: string,
  aircraftId?: string,
): Mission | undefined {
  return missions.find((m) => {
    if (!isOpenFerry(m)) return false;
    if (requestId && m.inboxRequestId && m.inboxRequestId !== requestId) return false;
    if (aircraftId && m.aircraftId !== aircraftId) return false;
    return sameIcao(m.originIcao, fromIcao) && sameIcao(m.destIcao, toIcao);
  });
}

export function inboundFerriesForCharter(
  missions: ReadonlyArray<Mission>,
  charter: Pick<Mission, "id" | "originIcao" | "destIcao" | "aircraftId" | "inboxRequestId">,
): Mission[] {
  return missions.filter((m) => {
    if (m.id === charter.id) return false;
    if (!isInboundPositioningFerry(m, charter)) return false;
    if (m.aircraftId !== charter.aircraftId) return false;
    if (m.parentMissionId && m.parentMissionId === charter.id) return true;
    if (charter.inboxRequestId && m.inboxRequestId === charter.inboxRequestId) return true;
    return false;
  });
}

export function staleInboundFerryIds(
  missions: ReadonlyArray<Mission>,
  request: { id?: string; originIcao: string; destIcao: string },
  keepIds: ReadonlySet<string>,
): string[] {
  return missions
    .filter((m) => {
      if (keepIds.has(m.id)) return false;
      if (request.id && m.inboxRequestId && m.inboxRequestId !== request.id) return false;
      if (request.id && !m.inboxRequestId) return false;
      return isInboundPositioningFerry(m, request);
    })
    .map((m) => m.id);
}

export function planFerryDeparture(input: {
  fromIcao: string;
  toIcao: string;
  eteMin: number;
  charterStd: Date;
  now: Date;
  hoursFor?: (icao: string | null | undefined) => AirportHours;
}): { departure: Date; shifted: boolean; warning?: string } {
  const turnMs = MIN_TURN_MIN * 60_000;
  const eteMs = Math.max(0, input.eteMin) * 60_000;
  const latest = new Date(input.charterStd.getTime() - turnMs - eteMs);
  const pad = new Date(input.now.getTime() + FERRY_PAD_MIN * 60_000);
  let nominal = latest;
  if (nominal.getTime() < input.now.getTime()) {
    nominal = pad;
  }

  const legal = (dep: Date) => ferryTimesLegal(input.fromIcao, input.toIcao, dep, input.eteMin, input.hoursFor);
  if (legal(nominal).legal) {
    return { departure: nominal, shifted: false };
  }

  const floor = latest.getTime() - FERRY_LOOKBACK_H * 60 * 60_000;
  for (let t = Math.min(nominal.getTime(), latest.getTime()); t >= floor; t -= STEP_MS) {
    const dep = new Date(t);
    if (dep.getTime() + eteMs + turnMs > input.charterStd.getTime()) continue;
    if (dep.getTime() < input.now.getTime() - 60_000) continue;
    if (legal(dep).legal) {
      return {
        departure: dep,
        shifted: true,
        warning: `CURFEW  ferry ${icaoKey(input.fromIcao)}–${icaoKey(input.toIcao)} shifted earlier to ${formatUtcDateTime(dep)} so dep/arr miss quiet hours.`,
      };
    }
  }

  const laterCeil = input.charterStd.getTime() - turnMs - eteMs;
  for (let t = Math.max(input.now.getTime(), pad.getTime()); t <= laterCeil; t += STEP_MS) {
    const dep = new Date(t);
    if (legal(dep).legal) {
      return {
        departure: dep,
        shifted: true,
        warning: `CURFEW  ferry ${icaoKey(input.fromIcao)}–${icaoKey(input.toIcao)} delayed to ${formatUtcDateTime(dep)} so dep/arr miss quiet hours.`,
      };
    }
  }

  const check = legal(latest);
  return {
    departure: latest,
    shifted: false,
    warning: curfewClashMessage(check, input.hoursFor) || `CURFEW  ferry ${icaoKey(input.fromIcao)}–${icaoKey(input.toIcao)} times overlap quiet hours.`,
  };
}

export function planCharterFerry(input: PlanCharterFerryInput): PlanCharterFerryResult {
  const now = input.now ?? new Date();
  const toIcao = icaoKey(input.toIcao);
  const fromIcao = icaoKey(input.fromIcao) || null;
  const needed = input.needed ?? Boolean(fromIcao && toIcao && fromIcao !== toIcao);
  const existing = input.existing ?? [];
  const keep = new Set<string>();

  if (!needed || !fromIcao || fromIcao === toIcao) {
    return {
      needed: false,
      ferry: null,
      reused: false,
      shifted: false,
      fromIcao,
      toIcao,
      cancelMissionIds: staleInboundFerryIds(existing, { id: input.linkRequestId, originIcao: toIcao, destIcao: "" }, keep),
    };
  }

  const reused = findOpenInboundFerry(existing, input.linkRequestId, fromIcao, toIcao, input.spec.id);
  const charterStd = asDate(input.charterStd) ?? new Date(now.getTime() + 4 * 60 * 60_000);
  const probe = buildRepositionDraft({
    spec: input.spec,
    fromIcao,
    toIcao,
    scenery: input.scenery,
    departure: charterStd,
    now,
    linkRequestId: input.linkRequestId,
    parentMissionId: input.parentMissionId,
  });
  if (!probe.ok) {
    return {
      needed: true,
      ferry: reused ?? null,
      reused: Boolean(reused),
      shifted: false,
      warning: probe.error,
      fromIcao,
      toIcao,
      cancelMissionIds: [],
    };
  }

  const timed = planFerryDeparture({
    fromIcao,
    toIcao,
    eteMin: probe.draft.eteMin,
    charterStd,
    now,
    hoursFor: input.hoursFor,
  });
  const built = buildRepositionDraft({
    spec: input.spec,
    fromIcao,
    toIcao,
    scenery: input.scenery,
    departure: timed.departure,
    now,
    linkRequestId: input.linkRequestId,
    parentMissionId: input.parentMissionId,
  });
  if (!built.ok) {
    return {
      needed: true,
      ferry: reused ?? null,
      reused: Boolean(reused),
      shifted: timed.shifted,
      warning: built.error,
      fromIcao,
      toIcao,
      cancelMissionIds: [],
    };
  }

  const draft: DraftMission = {
    ...built.draft,
    weatherNote: timed.shifted ? "Reposition ferry · curfew-adjusted" : built.draft.weatherNote,
    dispatchWarning: timed.warning,
  };
  if (reused) {
    const ferry: Mission = {
      ...reused,
      ...draft,
      id: reused.id,
      createdAt: reused.createdAt,
      status: reused.status,
      acceptedAt: reused.acceptedAt ?? now.toISOString(),
      parentMissionId: input.parentMissionId ?? reused.parentMissionId,
    };
    keep.add(ferry.id);
    return {
      needed: true,
      ferry,
      reused: true,
      shifted: timed.shifted,
      warning: timed.warning,
      fromIcao,
      toIcao,
      cancelMissionIds: staleInboundFerryIds(
        existing,
        { id: input.linkRequestId, originIcao: toIcao, destIcao: "" },
        keep,
      ),
    };
  }

  const ferry: Mission = { ...draft, status: "pending", acceptedAt: now.toISOString() };
  keep.add(ferry.id);
  return {
    needed: true,
    ferry,
    reused: false,
    shifted: timed.shifted,
    warning: timed.warning,
    fromIcao,
    toIcao,
    cancelMissionIds: staleInboundFerryIds(
      existing,
      { id: input.linkRequestId, originIcao: toIcao, destIcao: "" },
      keep,
    ),
  };
}

export type FerryLink = {
  missionId?: string;
  ferryMissionId?: string;
  returnFerryMissionId?: string;
  superseded?: boolean;
  status?: string;
};

function isOpenBoardLeg(m: Pick<Mission, "status">): boolean {
  return m.status === "pending" || m.status === "in-progress";
}

function openFerryLinks(assignments: readonly FerryLink[] | undefined): FerryLink[] {
  return (assignments ?? []).filter((row) => {
    if (row.superseded) return false;
    if (!row.status) return true;
    return row.status === "pending" || row.status === "held" || row.status === "accepted";
  });
}

function stdMsOf(iso: string | undefined): number {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? t : NaN;
}

function staMsOf(row: Pick<Mission, "departureIso" | "eteMin">): number {
  return stdMsOf(row.departureIso) + Math.max(25, row.eteMin) * 60_000;
}

/** Charter this ferry was filed for. Assignment links win, then parent id, then the shared RFQ. */
export function charterForFerry(
  missions: readonly Mission[],
  ferry: Pick<Mission, "id" | "flightType" | "parentMissionId" | "inboxRequestId">,
  assignments?: readonly FerryLink[],
): Mission | null {
  if (ferry.flightType !== "ferry") return null;
  const linked = openFerryLinks(assignments).find(
    (row) => row.ferryMissionId === ferry.id || row.returnFerryMissionId === ferry.id,
  );
  const wanted = linked?.missionId || ferry.parentMissionId;
  if (wanted) {
    const hit = missions.find((m) => m.id === wanted && m.flightType !== "ferry" && isOpenBoardLeg(m));
    if (hit) return hit;
  }
  if (ferry.inboxRequestId) {
    return (
      missions.find(
        (m) => m.inboxRequestId === ferry.inboxRequestId && m.flightType !== "ferry" && isOpenBoardLeg(m),
      ) ?? null
    );
  }
  return null;
}

/**
 * Positioning flies in before the charter. A return (home) ferry flies after it.
 * Stored `ferryMissionId` / `returnFerryMissionId` on the parent's assignment win;
 * time order, then route, is the fallback when the link was not filed.
 */
export function ferryRole(
  missions: readonly Mission[],
  ferry: Mission,
  assignments?: readonly FerryLink[],
): "positioning" | "return" | null {
  if (ferry.flightType !== "ferry" || ferry.isMaintenance) return null;
  const links = openFerryLinks(assignments);
  if (links.some((row) => row.returnFerryMissionId === ferry.id)) return "return";
  if (links.some((row) => row.ferryMissionId === ferry.id)) return "positioning";
  const parent = charterForFerry(missions, ferry, assignments);
  if (!parent) return null;
  const ferryStd = stdMsOf(ferry.departureIso);
  const parentStd = stdMsOf(parent.departureIso);
  if (!Number.isFinite(ferryStd) || !Number.isFinite(parentStd)) return null;
  const ferrySta = staMsOf(ferry);
  const parentSta = staMsOf(parent);
  if (ferrySta <= parentStd + 60_000) return "positioning";
  if (ferryStd + 60_000 >= parentSta) return "return";
  if (sameIcao(ferry.originIcao, parent.destIcao)) return "return";
  if (sameIcao(ferry.destIcao, parent.originIcao) && !sameIcao(ferry.originIcao, parent.destIcao)) return "positioning";
  return null;
}

/** Charter plus the positioning and return ferries filed with it, in fly order. */
export function tripUnit(
  missions: readonly Mission[],
  id: string,
  assignments?: readonly FerryLink[],
): Mission[] {
  const seed = missions.find((m) => m.id === id);
  if (!seed) return [];
  const charter = seed.flightType === "ferry" ? charterForFerry(missions, seed, assignments) : seed;
  if (!charter || charter.flightType === "ferry") return [seed];
  const links = openFerryLinks(assignments);
  const asg =
    links.find((row) => row.missionId === charter.id) ??
    links.find((row) => row.ferryMissionId === seed.id || row.returnFerryMissionId === seed.id);
  const ferryById = (missionId: string | undefined) =>
    missionId
      ? missions.find((m) => m.id === missionId && m.flightType === "ferry" && isOpenBoardLeg(m))
      : undefined;
  const linked = missions.filter((m) => {
    if (m.id === charter.id || m.flightType !== "ferry" || m.isMaintenance || !isOpenBoardLeg(m)) return false;
    if (m.parentMissionId === charter.id) return true;
    return Boolean(charter.inboxRequestId) && m.inboxRequestId === charter.inboxRequestId;
  });
  const positioning =
    ferryById(asg?.ferryMissionId) ?? linked.find((m) => ferryRole(missions, m, assignments) === "positioning");
  const home =
    ferryById(asg?.returnFerryMissionId) ??
    linked.find((m) => m.id !== positioning?.id && ferryRole(missions, m, assignments) === "return");
  return [positioning, charter, home].filter((m): m is Mission => Boolean(m));
}

/** When a charter STD slips, keep its inbound / empty-return ferries on the same family. */
export function shiftDependentFerries(
  missions: ReadonlyArray<Mission>,
  charter: Pick<Mission, "id" | "originIcao" | "destIcao" | "departureIso" | "eteMin" | "inboxRequestId">,
  now = new Date(),
  opts?: {
    afterMs?: number;
    /** A move past 24 hours or onto another duty day is skipped unless this is set. */
    confirmed?: boolean;
    batchId?: string;
    cause?: SlipCause;
    causeMissionId?: string | null;
    at?: string;
    onBlocked?: (request: SlipConfirmRequest) => void;
  },
): Mission[] {
  const charterStd = asDate(charter.departureIso);
  if (!charterStd) return [...missions];
  const at = opts?.at ?? now.toISOString();
  const batchId = opts?.batchId ?? `ferry-${now.getTime().toString(36)}`;
  return missions.map((m) => {
    if (m.id === charter.id) return m;
    if (m.status === "cancelled" || m.status === "completed") return m;
    if (m.flightType !== "ferry" || m.isMaintenance) return m;
    if (opts?.afterMs != null) {
      const dep = stdMsOf(m.departureIso);
      if (Number.isFinite(dep) && dep + 1000 < opts.afterMs) return m;
    }
    const linked =
      m.parentMissionId === charter.id ||
      (Boolean(charter.inboxRequestId) && m.inboxRequestId === charter.inboxRequestId);
    if (!linked) return m;
    let nextIso = "";
    if (sameIcao(m.destIcao, charter.originIcao) && !sameIcao(m.originIcao, charter.destIcao)) {
      const timed = planFerryDeparture({
        fromIcao: m.originIcao,
        toIcao: m.destIcao,
        eteMin: m.eteMin,
        charterStd,
        now,
      });
      nextIso = timed.departure.toISOString();
      if (!opts?.confirmed) {
        const held = confirmRequestFor(m, nextIso, charter.id);
        if (held) {
          opts?.onBlocked?.(held);
          return m;
        }
      }
      const slipped = recordSlip(m, nextIso, {
        cause: opts?.cause ?? "ferry",
        causeMissionId: opts?.causeMissionId ?? charter.id,
        at,
        batchId,
      });
      return { ...slipped, dispatchWarning: timed.warning ?? m.dispatchWarning };
    }
    if (sameIcao(m.originIcao, charter.destIcao)) {
      const arr = new Date(charterStd.getTime() + Math.max(25, charter.eteMin) * 60_000);
      nextIso = new Date(arr.getTime() + MIN_TURN_MIN * 60_000).toISOString();
      if (!opts?.confirmed) {
        const held = confirmRequestFor(m, nextIso, charter.id);
        if (held) {
          opts?.onBlocked?.(held);
          return m;
        }
      }
      return recordSlip(m, nextIso, {
        cause: opts?.cause ?? "ferry",
        causeMissionId: opts?.causeMissionId ?? charter.id,
        at,
        batchId,
      });
    }
    return m;
  });
}
